import React, { useState, useEffect } from 'react';
import api from '../../../../api';
import { TailSpin } from 'react-loader-spinner';
import { useAuth } from '../../../Layouts/context/Authcontext';
import dayjs from 'dayjs';
import TATFilter from './TATFilter';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';

const TATMetricsCard = () => {
  const { accessToken } = useAuth();

  const apiStartOfMonth = dayjs().startOf('month').format('MM/DD/YYYY');
  const apiToday = dayjs().format('MM/DD/YYYY');

  const [tatData, setTatData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState([apiStartOfMonth, apiToday]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedScope, setSelectedScope] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchData = async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);

    const params = {
      startDate: dateRange[0],
      endDate: dateRange[1],
    };
    if (selectedClient) params.clientId = selectedClient;
    if (selectedScope) params.scopeId = selectedScope;

    try {
      const baseUrl = getRoundRobinBaseUrl();
      const response = await api.get(`${baseUrl}/managerDashboard/getTAT`, {
        params,
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const res = response.data;

      // Prefer unified backend result (array). If not, fall back to [].
      const unified = Array.isArray(res?.data) ? res.data : [];
      setTatData(unified);
    } catch (err) {
      console.error('❌ Fetch error:', err);
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [accessToken, dateRange, selectedClient, selectedScope]);

  const handleClientSelect = (clientId) => setSelectedClient(clientId);
  const handleScopeSelect = (scopeId) => setSelectedScope(scopeId);
  const handleFromDateSelect = (fromDate) =>
    setDateRange([dayjs(fromDate).format('MM/DD/YYYY'), dateRange[1]]);
  const handleToDateSelect = (toDate) =>
    setDateRange([dateRange[0], dayjs(toDate).format('MM/DD/YYYY')]);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = tatData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(tatData.length / rowsPerPage);
  const showingFrom = tatData.length === 0 ? 0 : indexOfFirstRow + 1;
  const showingTo = Math.min(indexOfLastRow, tatData.length);

  const sumStatuses = (row) =>
    (row.Completed ?? 0) +
    (row.Pending ?? 0) +
    (row['Pending With Clarification'] ?? 0);

  return (
    <div className="w-full p-4">
      <div className="mb-8">
        <TATFilter
          selectedClientId={handleClientSelect}
          selectedScopeId={handleScopeSelect}
          selectedFromDate={handleFromDateSelect}
          selectedtoDate={handleToDateSelect}
        />
      </div>

      {loading ? (
        <div className="flex justify-center items-center">
          <TailSpin height="50" width="50" color="#1A2B5C" ariaLabel="loading" />
        </div>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : (
        <>
          <div className="overflow-x-auto mb-10">
            <h2 className="font-bold text-lg mb-2 text-[#1A2B5C]">
              Turnaround Time & File Status Metrics
            </h2>
            <table className="min-w-full bg-white border border-gray-300 rounded-lg shadow-md table-fixed">
              <thead>
                <tr className="bg-gradient-to-r from-[#1A2B5C] via-[#EE1D23] to-[#1A2B5C] text-white">
                  <th className="px-4 py-2">Client</th>
                  <th className="px-4 py-2">Scope</th>
                  <th className="px-4 py-2">Total Files</th>
                  <th className="px-4 py-2">Completed</th>
                  <th className="px-4 py-2">Pending</th>
                  <th className="px-4 py-2">Pending With Clarification</th>
                  <th className="px-4 py-2">0–12 hrs</th>
                  <th className="px-4 py-2">12–24 hrs</th>
                  <th className="px-4 py-2">24–36 hrs</th>
                  <th className="px-4 py-2">36–48 hrs</th>
                  <th className="px-4 py-2">&gt;48 hrs</th>
                </tr>
              </thead>
              <tbody>
                {currentRows.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="text-center py-4">
                      No data available
                    </td>
                  </tr>
                ) : (
                  currentRows.map((row, index) => {
                    const total = row.totalFiles ?? 0;
                    const statusSum = sumStatuses(row);
                    const mismatch = total !== statusSum;

                    return (
                      <tr key={index} className="text-center border-b hover:bg-gray-50 transition">
                        <td className="px-4 py-2">{row.client || '-'}</td>
                        <td className="px-4 py-2">{row.scope || '-'}</td>
                        <td className="px-4 py-2">
                          {total}
                          {mismatch && (
                            <span
                              title={`Sum of statuses = ${statusSum}`}
                              className="ml-2 text-red-600 font-semibold"
                            >
                              !
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-green-700 font-medium">
                          {row.Completed ?? 0}
                        </td>
                        <td className="px-4 py-2 text-yellow-700 font-medium">
                          {row.Pending ?? 0}
                        </td>
                        <td className="px-4 py-2 text-orange-700 font-medium">
                          {row['Pending With Clarification'] ?? 0}
                        </td>
                        <td className="px-4 py-2">
                          {row.zeroToTwelveHoursCompletedCount ?? 0}
                        </td>
                        <td className="px-4 py-2">
                          {row.twelveToTwentyFourHoursCompletedCount ?? 0}
                        </td>
                        <td className="px-4 py-2">
                          {row.twentyFourToThirtySixHoursCompletedCount ?? 0}
                        </td>
                        <td className="px-4 py-2">
                          {row.thirtySixToFortyEightHoursCompletedCount ?? 0}
                        </td>
                        <td className="px-4 py-2">
                          {row.greaterThanFortyEightHoursCompletedCount ?? 0}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center mt-4 w-full">
            <div className="text-sm mb-2 md:mb-0">
              Showing {showingFrom} to {showingTo} of {tatData.length} entries
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 bg-gray-300 rounded disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm">Page {currentPage} of {totalPages || 1}</span>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="px-3 py-1 bg-gray-300 rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>

          <div className="flex justify-center mt-2 w-full">
            <div className="flex items-center space-x-2">
              <label className="text-sm">Rows per page:</label>
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border rounded px-2 py-1"
              >
                {[10, 20, 50, 100].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TATMetricsCard;
